<template>
  <section class="statement-section">
    <div class="statement-section__header">
      <div class="statement-section__marker">4</div>
      <div>
        <h3 class="statement-section__title">Аттестат</h3>
        <p class="statement-section__description">
          Предметы и оценки из школьного аттестата
        </p>
      </div>
    </div>

    <div class="statement-fields">
      <v-select autocomplete="one-time-code" v-model="formData.educationBase" :items="$model.educationBases" item-title="short_name"
        item-value="type_ed" variant="outlined" density="comfortable" required>
        <template #label>
          База образования <span class="statement-required">*</span>
        </template>
      </v-select>

      <v-select autocomplete="one-time-code" v-model="formData.previousEducationLevel" :items="previousEducationLevels" item-title="value"
        item-value="key" variant="outlined" density="comfortable" label="Уровень предыдущего образования" />

      <v-radio-group v-model="formData.foreignEducationInstitution" class="statement-radio-group"
        label="Иностранное образовательное учреждение?" inline>
        <v-radio label="Да" :value="true" />
        <v-radio label="Нет" :value="false" />
      </v-radio-group>

      <div class="statement-inline-fields statement-inline-fields--personal">
        <v-text-field autocomplete="one-time-code" v-model="formData.graduationYear" variant="outlined" density="comfortable"
          inputmode="numeric" maxlength="4" :rules="graduationYearRules" :required="graduationYearRequired"
          @update:model-value="value => $emit('normalize-graduation-year', value)">
          <template #label>
            Год окончания учебного заведения <span v-if="graduationYearRequired" class="statement-required">*</span>
          </template>
        </v-text-field>

        <v-text-field autocomplete="one-time-code" v-model="formData.certificateReceivedDate" class="statement-date-field" type="date"
          variant="outlined" density="comfortable" :min="documentDateMin" :max="documentDateMax"
          :rules="documentDateRules" :required="certificateReceivedDateRequired">
          <template #label>
            Дата получения аттестата <span v-if="certificateReceivedDateRequired" class="statement-required">*</span>
          </template>
        </v-text-field>
      </div>

      <div class="statement-subsection">
        <h4 class="statement-subsection__title">Адрес образовательного учреждения</h4>
        <div class="statement-fields">
          <v-text-field autocomplete="one-time-code" v-model="formData.educationInstitutionAddress.country" label="Страна" variant="outlined"
            density="comfortable" />
          <v-autocomplete autocomplete="one-time-code" v-model="formData.educationInstitutionAddress.region" :items="$model.regionList"
            item-title="fullname" item-value="code" label="Регион" variant="outlined" density="comfortable" clearable />
          <v-text-field autocomplete="one-time-code" v-model="formData.educationInstitutionAddress.district" label="Район" variant="outlined"
            density="comfortable" />
          <v-text-field autocomplete="one-time-code" v-model="formData.educationInstitutionAddress.locality" label="Населенный пункт"
            variant="outlined" density="comfortable" />
          <v-text-field autocomplete="one-time-code" v-model="formData.educationInstitutionAddress.institutionName"
            label="Наименование образовательного учреждения" variant="outlined" density="comfortable" />
        </div>
      </div>

      <v-autocomplete autocomplete="one-time-code" v-model="formData.educationDocumentId" :items="$model.educationDocuments" item-title="name"
        item-value="code" label="Наименование документа" variant="outlined" density="comfortable" clearable />

      <div class="statement-inline-fields statement-inline-fields--personal">
        <v-text-field autocomplete="one-time-code" v-model="formData.educationDocumentSeries"
          label="Серия аттестата/свидетельства/диплома (если есть)" variant="outlined" density="comfortable" />

        <v-text-field autocomplete="one-time-code" v-model="formData.educationDocumentNumber"
          variant="outlined" density="comfortable" :required="educationDocumentNumberRequired"
          :rules="educationDocumentNumberRules">
          <template #label>
            Номер аттестата/свидетельства <span v-if="educationDocumentNumberRequired" class="statement-required">*</span>
          </template>
        </v-text-field>
      </div>

      <v-radio-group v-model="formData.certificateWithHonors" class="statement-radio-group" label="С отличием" inline>
        <v-radio label="Да" :value="true" />
        <v-radio label="Нет" :value="false" />
      </v-radio-group>

      <v-autocomplete autocomplete="one-time-code" v-model="formData.selectedSubjects" :items="subjectsMaster" item-title="name" item-value="id"
        multiple chips clearable hint="Выберите предметы и укажите оценки ниже" variant="outlined" density="comfortable">
        <template #label>
          Предметы школьного аттестата <span v-if="certificateGradesRequired" class="statement-required">*</span>
        </template>
      </v-autocomplete>

      <div v-if="formData.selectedSubjects.length" class="statement-subjects">
        <div v-for="subject in formData.selectedSubjects" :key="subject" class="statement-subject-row">
          <div class="statement-subject-row__name">
            {{ getSubjectName(subject) }}
          </div>
          <v-text-field autocomplete="one-time-code" :model-value="formData.subjectGrades[subject]" variant="outlined" density="comfortable"
            inputmode="numeric" :rules="subjectGradeRules" hide-details="auto"
            @update:model-value="value => $emit('update-subject-grade', subject, value)">
            <template #label>
              Оценка <span v-if="certificateGradesRequired" class="statement-required">*</span>
            </template>
          </v-text-field>
        </div>

        <div v-if="certificateGradesRequired || hasFilledCertificateGrades" class="statement-grade-average">
          Средний балл: <strong>{{ subjectGradeAverage }}</strong>
        </div>
      </div>

      <v-text-field v-if="!certificateGradesRequired && !hasFilledCertificateGrades"
        autocomplete="one-time-code"
        :model-value="formData.certificateAverage"
        label="Средний балл"
        variant="outlined"
        density="comfortable"
        inputmode="decimal"
        :rules="certificateAverageRules"
        @update:model-value="value => $emit('update-certificate-average', value)"
        @blur="$emit('normalize-certificate-average')"
      />

      <template v-if="showGiaResults">
        <v-autocomplete autocomplete="one-time-code" v-model="formData.selectedGiaSubjects" :items="subjectsGia" item-title="name" item-value="id"
          multiple chips clearable hint="Выберите предметы ГИА и укажите результаты ниже" variant="outlined"
          density="comfortable">
          <template #label>
            Результаты ГИА <span v-if="giaResultsRequired" class="statement-required">*</span>
          </template>
        </v-autocomplete>

        <div v-if="formData.selectedGiaSubjects.length" class="statement-subjects">
          <div v-for="subject in formData.selectedGiaSubjects" :key="subject" class="statement-subject-row">
            <div class="statement-subject-row__name">
              {{ getGiaSubjectName(subject) }}
            </div>
            <v-text-field autocomplete="one-time-code" :model-value="formData.giaResults[subject]" variant="outlined" density="comfortable"
              inputmode="numeric" :rules="giaResultRules" hide-details="auto"
              @update:model-value="value => $emit('update-gia-result', subject, value)">
              <template #label>
                Результат <span v-if="giaResultsRequired" class="statement-required">*</span>
              </template>
            </v-text-field>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CertificateSection',
  emits: [
    'normalize-graduation-year',
    'update-subject-grade',
    'update-gia-result',
    'update-certificate-average',
    'normalize-certificate-average'
  ],
  props: {
    formData: {
      type: Object,
      required: true
    },
    previousEducationLevels: {
      type: Array,
      required: true
    },
    subjectsMaster: {
      type: Array,
      required: true
    },
    subjectsGia: {
      type: Array,
      default: () => []
    },
    showGiaResults: {
      type: Boolean,
      default: false
    },
    graduationYearRules: {
      type: Array,
      required: true
    },
    documentDateMin: {
      type: String,
      required: true
    },
    documentDateMax: {
      type: String,
      required: true
    },
    documentDateRules: {
      type: Array,
      required: true
    },
    subjectGradeRules: {
      type: Array,
      required: true
    },
    giaResultRules: {
      type: Array,
      required: true
    },
    certificateAverageRules: {
      type: Array,
      required: true
    },
    subjectGradeAverage: {
      type: String,
      required: true
    },
    graduationYearRequired: {
      type: Boolean,
      default: true
    },
    certificateReceivedDateRequired: {
      type: Boolean,
      default: true
    },
    educationDocumentNumberRequired: {
      type: Boolean,
      default: true
    },
    certificateGradesRequired: {
      type: Boolean,
      default: true
    },
    hasFilledCertificateGrades: {
      type: Boolean,
      default: false
    },
    giaResultsRequired: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    educationDocumentNumberRules() {
      return [
        value => !this.educationDocumentNumberRequired || Boolean(String(value || '').trim()) || 'Заполните поле'
      ]
    }
  },
  methods: {
    /**
     * Resolves certificate subject ids to names for grade rows.
     */
    getSubjectName(subjectId) {
      const subject = this.subjectsMaster.find(item => String(item.id) === String(subjectId))

      return subject ? subject.name : subjectId
    },
    /**
     * Resolves GIA subject ids to names for result rows.
     */
    getGiaSubjectName(subjectId) {
      const subject = this.subjectsGia.find(item => String(item.id) === String(subjectId))

      return subject ? subject.name : subjectId
    }
  }
}
</script>

<style>
.statement-subjects {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.statement-subject-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(120px, 160px);
  gap: 12px;
  align-items: start;
  padding: 10px 12px;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
  background: #ffffff;
}

.statement-subject-row__name {
  min-width: 0;
  padding-top: 12px;
  color: #344054;
  overflow-wrap: anywhere;
}

.statement-grade-average {
  align-self: flex-end;
  color: #344054;
}

@media (max-width: 640px) {
  .statement-subject-row {
    grid-template-columns: minmax(0, 1fr);
  }

  .statement-grade-average {
    align-self: flex-start;
  }
}
</style>
